// AI Service Integration
// This file contains functions for AI service integration with multiple providers
//
// Environment Variables Required:
// - VITE_AI_PROVIDER: 'google' | 'chatgpt' | 'openrouter' (default: 'google')
// - VITE_GOOGLE_AI_API_KEY: Google AI API key for Gemini
// - VITE_OPENROUTER_API_KEY: OpenRouter API key
// - VITE_OPENROUTER_MODEL: Model to use (default: 'openai/gpt-3.5-turbo')

export interface AIResponse {
  content: string;
  success: boolean;
  error?: string;
}

// Google AI Integration (Gemini Pro)
export async function callGoogleAI(message: string): Promise<AIResponse> {
  try {
    const apiKey = import.meta.env.VITE_GOOGLE_AI_API_KEY;
    
    if (!apiKey) {
      return {
        content: "Google AI API key not configured. Please add VITE_GOOGLE_AI_API_KEY to your environment variables.",
        success: false,
        error: 'API key missing'
      };
    }

    const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contents: [{ 
          parts: [{ text: message }],
          role: "user"
        }],
        generationConfig: {
          temperature: AI_CONFIG.temperature,
          maxOutputTokens: AI_CONFIG.maxTokens,
        },
        safetySettings: [
          {
            category: "HARM_CATEGORY_HATE_SPEECH",
            threshold: "BLOCK_NONE"
          },
          {
            category: "HARM_CATEGORY_DANGEROUS_CONTENT", 
            threshold: "BLOCK_NONE"
          },
          {
            category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
            threshold: "BLOCK_NONE"
          },
          {
            category: "HARM_CATEGORY_HARASSMENT",
            threshold: "BLOCK_NONE"
          }
        ]
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Google AI API error: ${response.status} - ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();
    
    // Debug logging
    console.log('Gemini API Response:', JSON.stringify(data, null, 2));
    
    // Check for prompt feedback errors
    if (data.promptFeedback && data.promptFeedback.blockReason) {
      throw new Error(`Prompt blocked: ${data.promptFeedback.blockReason}`);
    }
    
    // Check if candidates exist and have content
    if (!data.candidates || data.candidates.length === 0) {
      throw new Error('No candidates returned from Google AI');
    }
    
    const candidate = data.candidates[0];
    
    // Check finish reason
    if (candidate.finishReason && candidate.finishReason !== 'STOP') {
      throw new Error(`Content generation stopped: ${candidate.finishReason}`);
    }
    
    // Check safety ratings
    if (candidate.safetyRatings) {
      const blockedCategories = candidate.safetyRatings.filter(
        (rating: any) => rating.probability === 'HIGH' || rating.probability === 'MEDIUM'
      );
      if (blockedCategories.length > 0) {
        throw new Error(`Content blocked due to safety: ${blockedCategories.map((c: any) => c.category).join(', ')}`);
      }
    }
    
    // Extract content safely
    if (!candidate.content || !candidate.content.parts || candidate.content.parts.length === 0) {
      throw new Error('No content parts found in candidate response');
    }
    
    const content = candidate.content.parts[0]?.text || 'No response generated';
    
    return {
      content: content.trim(),
      success: true
    };
  } catch (error) {
    console.error('Google AI API Error:', error);
    return {
      content: `Sorry, I'm having trouble connecting to Google AI right now. ${error instanceof Error ? error.message : 'Unknown error'}`,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// OpenRouter Integration
export async function callOpenRouter(message: string): Promise<AIResponse> {
  try {
    const apiKey = import.meta.env.VITE_OPENROUTER_API_KEY;
    
    if (!apiKey) {
      return {
        content: "OpenRouter API key not configured. Please add VITE_OPENROUTER_API_KEY to your environment variables.",
        success: false,
        error: 'API key missing'
      };
    }

    const model = import.meta.env.VITE_OPENROUTER_MODEL || 'openai/gpt-3.5-turbo';
    
    const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'HTTP-Referer': window.location.origin,
        'X-Title': 'Join Americation Admin'
      },
      body: JSON.stringify({
        model: model,
        messages: [{ role: 'user', content: message }],
        max_tokens: AI_CONFIG.maxTokens,
        temperature: AI_CONFIG.temperature
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`OpenRouter API error: ${response.status} - ${errorData.error?.message || response.statusText}`);
    }

    const data = await response.json();
    
    // Debug logging
    console.log('OpenRouter API Response:', JSON.stringify(data, null, 2));
    
    if (!data.choices || data.choices.length === 0) {
      throw new Error('No choices returned from OpenRouter');
    }

    const content = data.choices[0]?.message?.content || 'No response generated';
    
    return {
      content: content.trim(),
      success: true
    };
  } catch (error) {
    console.error('OpenRouter API Error:', error);
    return {
      content: `Sorry, I'm having trouble connecting to OpenRouter right now. ${error instanceof Error ? error.message : 'Unknown error'}`,
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// ChatGPT Integration (placeholder)
export async function callChatGPT(message: string): Promise<AIResponse> {
  try {
    // Replace with actual ChatGPT API call
    // Example: const response = await fetch('https://api.openai.com/v1/chat/completions', {
    //   method: 'POST',
    //   headers: {
    //     'Authorization': `Bearer ${process.env.OPENAI_API_KEY}`,
    //     'Content-Type': 'application/json',
    //   },
    //   body: JSON.stringify({
    //     model: 'gpt-3.5-turbo',
    //     messages: [{ role: 'user', content: message }],
    //     max_tokens: 150
    //   })
    // });
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return {
      content: `ChatGPT Response: I received your message "${message}". This is a placeholder response. Configure your OpenAI API key to get real responses.`,
      success: true
    };
  } catch (error) {
    return {
      content: "Sorry, I'm having trouble connecting to ChatGPT right now.",
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Main AI service function - switch between providers
export async function getAIResponse(message: string, provider?: 'google' | 'chatgpt' | 'openrouter'): Promise<AIResponse> {
  // Use environment variable if no provider specified
  const selectedProvider = provider || import.meta.env.VITE_AI_PROVIDER || 'openrouter';
  
  switch (selectedProvider) {
    case 'google':
      return callGoogleAI(message);
    case 'chatgpt':
      return callChatGPT(message);
    case 'openrouter':
      return callOpenRouter(message);
    default:
      return callOpenRouter(message);
  }
}

// Configuration helper
export const AI_CONFIG = {
  defaultProvider: 'openrouter' as const,
  providers: ['google', 'chatgpt', 'openrouter'] as const,
  maxTokens: 1000,
  temperature: 0.7,
};
